import axios from 'axios';
import { UserSettingsSchema, type UserSettingsResponse } from '../schemas/apiResponse.schemas';
import { validateResponse } from './validateResponse';

const API_BASE = '/api/user-settings';

export interface UserSettings {
  colorMode: boolean;
  cameraOpensOrderDetails: boolean;
  collapseLocatedBundles: boolean;
  photographPackingItems: boolean;
  printScanOrderLabel: boolean;
  scanSuggestBoxSize: boolean;
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  colorMode: false,
  cameraOpensOrderDetails: false,
  collapseLocatedBundles: true,
  photographPackingItems: false,
  printScanOrderLabel: false,
  scanSuggestBoxSize: true,
};

export interface UserSettingsPatch {
  colorMode?: boolean;
  cameraOpensOrderDetails?: boolean;
  collapseLocatedBundles?: boolean;
  photographPackingItems?: boolean;
  printScanOrderLabel?: boolean;
  scanSuggestBoxSize?: boolean;
}

export function mapUserSettingsResponse(data: UserSettingsResponse): UserSettings {
  return {
    colorMode: data.color_mode,
    cameraOpensOrderDetails: data.camera_opens_order_details,
    collapseLocatedBundles: data.collapse_located_bundles,
    photographPackingItems: data.photograph_packing_items,
    printScanOrderLabel: data.print_scan_order_label,
    scanSuggestBoxSize: data.scan_suggest_box_size,
  };
}

export async function fetchUserSettings(): Promise<UserSettings> {
  const response = await axios.get(API_BASE);
  const parsed = validateResponse(UserSettingsSchema, response.data, 'fetchUserSettings');
  return mapUserSettingsResponse(parsed);
}

const SETTINGS_CACHE_PREFIX = 'wms_user_settings:';

function cacheKey(email: string): string {
  return `${SETTINGS_CACHE_PREFIX}${email.trim().toLowerCase()}`;
}

function readCollapseLocatedBundles(value: object): boolean {
  if (!('collapseLocatedBundles' in value)) return true;
  return typeof value.collapseLocatedBundles === 'boolean' ? value.collapseLocatedBundles : true;
}

function readPhotographPackingItems(value: object): boolean {
  if (!('photographPackingItems' in value)) return false;
  return typeof value.photographPackingItems === 'boolean' ? value.photographPackingItems : false;
}

/** Caches written before the beta split only carry `fulfillmentFlowBeta`. */
function readLegacyFulfillmentFlowBeta(value: object): boolean {
  if (!('fulfillmentFlowBeta' in value)) return false;
  return typeof value.fulfillmentFlowBeta === 'boolean' ? value.fulfillmentFlowBeta : false;
}

function readPrintScanOrderLabel(value: object): boolean {
  if (!('printScanOrderLabel' in value)) return readLegacyFulfillmentFlowBeta(value);
  return typeof value.printScanOrderLabel === 'boolean' ? value.printScanOrderLabel : false;
}

function readScanSuggestBoxSize(value: object): boolean {
  if (!('scanSuggestBoxSize' in value)) return readLegacyFulfillmentFlowBeta(value);
  return typeof value.scanSuggestBoxSize === 'boolean' ? value.scanSuggestBoxSize : false;
}

function parseCachedUserSettings(value: unknown): UserSettings | null {
  if (
    typeof value !== 'object'
    || value === null
    || !('colorMode' in value)
    || typeof value.colorMode !== 'boolean'
    || !('cameraOpensOrderDetails' in value)
    || typeof value.cameraOpensOrderDetails !== 'boolean'
  ) {
    return null;
  }
  return {
    colorMode: value.colorMode,
    cameraOpensOrderDetails: value.cameraOpensOrderDetails,
    collapseLocatedBundles: readCollapseLocatedBundles(value),
    photographPackingItems: readPhotographPackingItems(value),
    printScanOrderLabel: readPrintScanOrderLabel(value),
    scanSuggestBoxSize: readScanSuggestBoxSize(value),
  };
}

export function readCachedUserSettings(email: string): UserSettings | null {
  if (typeof window === 'undefined') return null;
  const trimmed = email.trim();
  if (!trimmed) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(trimmed));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parseCachedUserSettings(parsed);
  } catch {
    return null;
  }
}

export function writeCachedUserSettings(email: string, settings: UserSettings): void {
  if (typeof window === 'undefined') return;
  const trimmed = email.trim();
  if (!trimmed) return;
  window.localStorage.setItem(cacheKey(trimmed), JSON.stringify(settings));
}

/** The beta is not stored: it is on only while all three of its children are on. */
export function isFulfillmentFlowBetaOn(settings: UserSettings): boolean {
  return settings.printScanOrderLabel && settings.photographPackingItems && settings.scanSuggestBoxSize;
}

export function fulfillmentFlowBetaPatch(enabled: boolean): UserSettingsPatch {
  return {
    printScanOrderLabel: enabled,
    photographPackingItems: enabled,
    scanSuggestBoxSize: enabled,
  };
}

export async function patchUserSettings(patch: UserSettingsPatch): Promise<UserSettings> {
  const body: {
    color_mode?: boolean;
    camera_opens_order_details?: boolean;
    collapse_located_bundles?: boolean;
    photograph_packing_items?: boolean;
    print_scan_order_label?: boolean;
    scan_suggest_box_size?: boolean;
  } = {};
  if (patch.colorMode !== undefined) {
    body.color_mode = patch.colorMode;
  }
  if (patch.cameraOpensOrderDetails !== undefined) {
    body.camera_opens_order_details = patch.cameraOpensOrderDetails;
  }
  if (patch.collapseLocatedBundles !== undefined) {
    body.collapse_located_bundles = patch.collapseLocatedBundles;
  }
  if (patch.photographPackingItems !== undefined) {
    body.photograph_packing_items = patch.photographPackingItems;
  }
  if (patch.printScanOrderLabel !== undefined) {
    body.print_scan_order_label = patch.printScanOrderLabel;
  }
  if (patch.scanSuggestBoxSize !== undefined) {
    body.scan_suggest_box_size = patch.scanSuggestBoxSize;
  }
  const response = await axios.patch(API_BASE, body);
  const parsed = validateResponse(UserSettingsSchema, response.data, 'patchUserSettings');
  return mapUserSettingsResponse(parsed);
}
